/**
 * Data checks: raw rows in, cleaned parcels and a list of issues out.
 * Runs entirely in the browser; only `parcels` (kept fields) are uploaded.
 */
import { missingRequired } from "./mapping.ts";
import { rowsToParcels, stripRow, type CleanRow, type ColumnMapping, type ServiceMap, type UploadUnits } from "./schema.ts";
import type { Issue, Parcel } from "./types.ts";

export interface IntakeStats {
  sourceRows: number;
  keptRows: number;
  excludedRows: number;
  duplicateRows: number;
  parcels: number;
  errorCount: number;
  warningCount: number;
  dateFrom: string | null;
  dateTo: string | null;
}

export interface ServiceCount {
  raw: string;
  rows: number;
  mappedTo: string | null;
}

export interface IntakeResult {
  ok: boolean;
  /** Required fields with no column chosen; when non-empty nothing else runs */
  missingFields: string[];
  cleanRows: CleanRow[];
  parcels: Parcel[];
  issues: Issue[];
  stats: IntakeStats;
  services: ServiceCount[];
}

const median = (xs: number[]) => {
  if (xs.length === 0) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
};

export function runIntake(
  raw: Record<string, unknown>[],
  mapping: ColumnMapping,
  units: UploadUnits,
  serviceMap: ServiceMap = {},
): IntakeResult {
  const missingFields = missingRequired(mapping);
  const emptyStats: IntakeStats = {
    sourceRows: raw.length, keptRows: 0, excludedRows: 0, duplicateRows: 0, parcels: 0,
    errorCount: 0, warningCount: 0, dateFrom: null, dateTo: null,
  };
  if (missingFields.length > 0) {
    return { ok: false, missingFields, cleanRows: [], parcels: [], issues: [], stats: emptyStats, services: [] };
  }

  const issues: Issue[] = [];
  const clean: CleanRow[] = [];
  const seen = new Set<string>();
  let excluded = 0;
  let duplicates = 0;

  raw.forEach((r, idx) => {
    const { row, issues: rowIssues } = stripRow(r, idx + 1, mapping, units);
    issues.push(...rowIssues);
    if (!row) {
      excluded++;
      return;
    }
    const { row: _n, ...rest } = row;
    const key = JSON.stringify(rest);
    if (seen.has(key)) {
      duplicates++;
      issues.push({ severity: "warning", code: "duplicate_line", row: row.row, orderId: row.orderId, message: "Exact duplicate of an earlier line; dropped" });
      return;
    }
    seen.add(key);
    clean.push(row);

    const [l, w, h] = [row.dims.lengthCm, row.dims.widthCm, row.dims.heightCm].sort((a, b) => b - a);
    if (row.weightKg > 30) issues.push({ severity: "warning", code: "unusual_value", field: "weight", row: row.row, orderId: row.orderId, message: `Weight ${row.weightKg} kg is unusually high; check units` });
    if ((l ?? 0) > 150) issues.push({ severity: "warning", code: "unusual_value", field: "length", row: row.row, orderId: row.orderId, message: `Longest side ${l} cm is unusually long; check units` });
    if ((h ?? 1) < 0.1 || (w ?? 1) < 0.1) issues.push({ severity: "warning", code: "unusual_value", field: "height", row: row.row, orderId: row.orderId, message: "A side under 1 mm; check units" });
    if (row.costPence === 0) issues.push({ severity: "warning", code: "zero_cost", field: "cost", row: row.row, orderId: row.orderId, message: "Shipping cost is zero" });
  });

  // Whole-file unit sanity: a typical parcel weighing over 50 "kg" was almost certainly in grams.
  if (clean.length >= 5) {
    const mw = median(clean.map((r) => r.weightKg));
    const ml = median(clean.map((r) => Math.max(r.dims.lengthCm, r.dims.widthCm, r.dims.heightCm)));
    if (mw > 50) issues.push({ severity: "warning", code: "units_suspect", field: "weight", message: `Typical weight is ${mw} kg; the weight column may be in grams` });
    if (ml > 150) issues.push({ severity: "warning", code: "units_suspect", field: "length", message: `Typical longest side is ${ml} cm; sizes may be in millimetres` });
    if (mw > 0 && mw < 0.005) issues.push({ severity: "warning", code: "units_suspect", field: "weight", message: `Typical weight is ${mw} kg; the weight column may be in kg already` });
  }

  const { parcels, issues: parcelIssues } = rowsToParcels(clean, serviceMap);
  issues.push(...parcelIssues);

  const svc = new Map<string, number>();
  for (const r of clean) svc.set(r.carrierServiceRaw, (svc.get(r.carrierServiceRaw) ?? 0) + 1);
  const services = [...svc]
    .map(([rawName, rows]) => ({ raw: rawName, rows, mappedTo: serviceMap[rawName.toLowerCase()] ?? null }))
    .sort((a, b) => b.rows - a.rows);

  const dates = clean.map((r) => r.orderDate).sort();
  const errorCount = issues.filter((i) => i.severity === "error").length;
  const stats: IntakeStats = {
    sourceRows: raw.length,
    keptRows: clean.length,
    excludedRows: excluded,
    duplicateRows: duplicates,
    parcels: parcels.length,
    errorCount,
    warningCount: issues.length - errorCount,
    dateFrom: dates[0] ?? null,
    dateTo: dates.at(-1) ?? null,
  };
  return {
    ok: errorCount === 0 && services.every((s) => s.mappedTo !== null),
    missingFields: [],
    cleanRows: clean,
    parcels,
    issues,
    stats,
    services,
  };
}
